import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges, ChangeDetectorRef } from '@angular/core';

export interface ProfileUpdatePayload {
  userName: string;
  phoneNumber: string;
  birthDay: string | null;
}

@Component({
  selector: 'app-profile-dialog',
  templateUrl: './profile-dialog.component.html',
  styleUrls: ['./profile-dialog.component.css']
})
export class ProfileDialogComponent implements OnChanges {
  @Input() visible = false;
  @Input() userInfo: { id: number; userName: string; phoneNumber: string; birthDay: string } | null = null;
  @Input() submitting = false;
  @Input() hasError = false;
  @Input() errorMessage = '';

  @Output() closed = new EventEmitter<void>();
  @Output() submitted = new EventEmitter<ProfileUpdatePayload>();
  @Output() saveChanges = new EventEmitter<any>();

  isEditing = false;
  justSaved = false;
  draftUserName = '';
  draftPhoneNumber = '';
  draftBirthDay = '';
  localError = '';
  maxBirthDate: string;

  constructor(private cdr: ChangeDetectorRef) {
    const today = new Date();
    const year = today.getFullYear() - 15;
    const month = ('0' + (today.getMonth() + 1)).slice(-2);
    const day = ('0' + today.getDate()).slice(-2);
    this.maxBirthDate = `${year}-${month}-${day}`;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['visible']?.currentValue || changes['userInfo']) {
      this.isEditing = false; // Reset editing state on open
      this.justSaved = false;
      this.syncDraftFromUserInfo();
    }
    if (!this.visible) {
      this.localError = '';
    }
  }

  close(): void {
    this.localError = '';
    this.isEditing = false;
    this.justSaved = false;
    this.closed.emit();
  }

  get isDirty(): boolean {
    if (!this.userInfo) return false;
    const originalUserName = this.normalizeText(this.userInfo.userName || '');
    const originalPhoneNumber = this.normalizeText(String(this.userInfo.phoneNumber || '')).replace(/\D/g, '').slice(0, 10);
    // Hide success message automatically after 3 seconds
    setTimeout(() => {
      this.justSaved = false;
      this.cdr.markForCheck();
    }, 3000);
    const originalBirthDay = this.userInfo.birthDay || '';

    return (
      this.normalizeText(this.draftUserName) !== originalUserName ||
      this.normalizeText(this.draftPhoneNumber) !== originalPhoneNumber ||
      this.draftBirthDay !== originalBirthDay
    );
  }

  toggleEdit(): void {
    this.isEditing = !this.isEditing;
    this.justSaved = false;
    if (!this.isEditing) {
      this.syncDraftFromUserInfo(); // Revert changes if toggling off
    }
  }

  save(): void {
    this.submitted.emit({
      userName: this.draftUserName,
      phoneNumber: this.draftPhoneNumber,
      birthDay: this.draftBirthDay || null,
    });
    // After emitting, we expect the parent to handle submission.
    // We can revert the state here.
    this.isEditing = false;
    this.justSaved = true;
  }

  handleEditSave(): void {
    if (!this.isEditing) {
      this.toggleEdit();
    } else {
      this.save();
    }
  }

  onPhoneNumberInput(event: Event): void {
    const input = event.target as HTMLInputElement | null;
    const raw = input?.value ?? '';
    const digitsOnly = String(raw).replace(/\D/g, '').slice(0, 10);
    this.draftPhoneNumber = digitsOnly;
    if (input && input.value !== digitsOnly) {
      input.value = digitsOnly;
    }
  }

  private syncDraftFromUserInfo(): void {
    this.localError = '';
    this.justSaved = false;
    this.draftUserName = this.userInfo?.userName || '';
    this.draftPhoneNumber = String(this.userInfo?.phoneNumber || '').replace(/\D/g, '').slice(0, 10);
    this.draftBirthDay = this.userInfo?.birthDay || '';
    if (this.draftBirthDay) {
      this.draftBirthDay = this.draftBirthDay.split('T')[0];
    }
  }

  private normalizeText(value: string): string {
    return String(value || '').trim();
  }
}
